import React from 'react';
import { cn } from '@/lib/utils';

// U+1F527. Written as an escape so the glyph survives any editor or encoding
// that mangles astral-plane characters.
export const DEV_KIT_WRENCH = '\u{1F527}';

/**
 * The uppercase monospace `DEV` badge. Mirrors `.dev-kit-chip` in
 * `app/views/shared/_dev_kit_panel.html.erb` so the React and ERB dev-kit
 * surfaces carry an identical mark.
 */
export function DevKitChip({ className, ...rest }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded border border-dashed border-neutral-700 bg-neutral-100 px-1.5 py-0.5',
        'font-mono text-xs font-semibold leading-none tracking-[0.14em] text-neutral-900',
        'dark:border-neutral-600 dark:bg-neutral-1200 dark:text-neutral-400',
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true">DEV</span>
      {/* The visible mark is decorative shorthand; screen readers get the phrase it stands for. */}
      <span className="sr-only">Dev site only</span>
    </span>
  );
}

/**
 * Inline variant of the dev kit: the same dashed-border, wrench-and-chip
 * signature as the floating `_dev_kit_panel` surfaces, but it stays in the
 * document flow and never collapses, because the action it wraps belongs to
 * the surrounding step rather than to the page as a whole.
 */
export function DevKitInline({ children, className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-[10px] border-[1.5px] border-dashed border-neutral-700 bg-neutral-100 px-3.5 py-3',
        'dark:border-neutral-600 dark:bg-neutral-1200',
        className,
      )}
      {...rest}
    >
      <span className="flex items-center gap-1.5">
        <span aria-hidden="true" className="text-xs leading-none">
          {DEV_KIT_WRENCH}
        </span>
        <DevKitChip />
      </span>
      {children}
    </div>
  );
}
